import { LoginCredentials, LoginResponse } from '../../../domain/entities/token_entity';

/**
 * Interface para datasource remoto de autenticação
 */
export interface AuthRemoteDataSource {
  /**
   * Realiza login via API
   */
  login(credentials: LoginCredentials): Promise<LoginResponse>;

  /**
   * Realiza logout via API
   */
  logout(): Promise<void>;

  /**
   * Renova token via API
   */
  refreshToken(refreshToken: string): Promise<LoginResponse>;
}

/**
 * Implementação do datasource remoto usando HTTP
 */
export class HttpAuthDataSource implements AuthRemoteDataSource {
  constructor(
    private baseUrl: string,
    private httpClient: (url: string, options?: RequestInit) => Promise<Response>
  ) {}

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const entityType = credentials.entityType || 'master';
      const endpoint = `${this.baseUrl}/auth/${entityType}/login`;
      
      console.log(`🔗 Fazendo requisição para: ${endpoint}`);
      
      const response = await this.httpClient(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Importante: inclui cookies na requisição
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      });

      if (!response.ok) {
        // Tentar decodificar o corpo de erro, sem expor diretamente ao usuário
        const errorData = await response.json().catch(() => null);
        // Mapear códigos HTTP para mensagens amigáveis
        if (response.status === 401) {
          console.warn('Auth failed (401) from server:', errorData);
          throw new Error('Email ou senha inválidos.');
        }

        if (response.status === 422) {
          console.warn('Validation failed (422) from server:', errorData);
          throw new Error('Dados de login inválidos. Confira e tente novamente.');
        }

        // Outros erros: log para debug e mensagem genérica para a UI
        console.error('Login error response:', { status: response.status, body: errorData });
        throw new Error('Não foi possível efetuar login agora. Tente novamente mais tarde.');
      }

      const data = await response.json();
      
      // Verificar se a resposta tem a estrutura esperada
      if (data && data.status && data.status !== 200) {
        // API pode retornar mensagens amigáveis; logamos o detalhe e propagamos uma mensagem genérica
        console.warn('Login returned non-200 application status:', { status: data.status, message: data.message });
        throw new Error(data.message || 'Não foi possível efetuar login.');
      }
      
      console.log('✅ Resposta da API recebida:', { status: data.status, userId: data.user?.id, role: data.user?.role });
      
      return data;
    } catch (error) {
      // Já lançamos Errors amigáveis acima; aqui só logamos e propagamos uma mensagem segura
      console.error('❌ Erro na requisição de login:', error);
      if (error instanceof Error && error.message) {
        throw new Error(error.message);
      }
      throw new Error('Erro ao processar login.');
    }
  }

  async logout(): Promise<void> {
    try {
      const response = await this.httpClient(`${this.baseUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Inclui cookies para identificar a sessão
      });

      if (!response.ok) {
        throw new Error(`Logout failed: ${response.statusText}`);
      }
      
      console.log('✅ Logout realizado no servidor');
    } catch (error) {
      console.error('❌ Erro no logout:', error);
      throw new Error(`Logout request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    try {
      const response = await this.httpClient(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw new Error(`Token refresh request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
